import React, { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with a show/hide toggle. Mirrors the project's input
 * styling so it drops straight into Login / Reset pages without overrides.
 *
 * The eye icon is a real <button type="button"> — keyboard focusable,
 * announced to screen readers, and stops the parent form from submitting.
 */
const PasswordInput = forwardRef(function PasswordInput(
    {
        id = "password",
        value,
        onChange,
        autoComplete = "current-password",
        required = true,
        placeholder = "••••••••",
        testid = "password-input",
        ...rest
    },
    ref
) {
    const [visible, setVisible] = useState(false);
    const toggle = () => setVisible((v) => !v);
    return (
        <div className="relative">
            <input
                ref={ref}
                id={id}
                type={visible ? "text" : "password"}
                autoComplete={autoComplete}
                required={required}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full h-11 rounded-md bg-zinc-950/60 border border-zinc-800 pl-3 pr-11 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-all focus:border-zinc-500 focus:bg-zinc-900/60"
                {...rest}
            />
            <button
                type="button"
                onClick={toggle}
                tabIndex={0}
                aria-label={visible ? "Hide password" : "Show password"}
                aria-pressed={visible}
                data-testid={`${testid}-toggle`}
                className="absolute inset-y-0 right-0 px-3 inline-flex items-center text-zinc-500 hover:text-zinc-200 transition-colors"
            >
                {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
});

export default PasswordInput;
